import { ICollaborationController, SocketOptions } from './collaboration.types';
import { SOCKET_ERRORS, SOCKET_EVENTS } from './socket.config';

const io = require('socket.io-client');

/**
 *
 * @param {Object} controller a CollaborationController instance
 * @returns {Object} a socket.io object
 */
export const createSocket = (controller: ICollaborationController) => {
  const config = controller.socketConfig;
  const socketOptions: SocketOptions = {
    transports: ['websocket'],
    path: config.path,
    query: {
      token: config.token,
      infographicId: controller.metadata.id,
      workspaceId: controller.metadata.workspaceId ?? '',
    },
  };
  const socket = io.connect(config.domain, socketOptions);
  let recentActiveSocketId = ''; // for debug purposes
  let isSocketAttemptOnPageLoad = true;

  const getSocketId = () => {
    return socket.id ? socket.id : 'empty socket id';
  };

  /**
   * Handle reconnection
   */
  socket.on(SOCKET_EVENTS.RECONNECTING, (attemptNumber: number) => {
    // If this is disconnected because the user disabled share then don't prompt
    if (!controller.isTeamShared) return;

    isSocketAttemptOnPageLoad = false;

    console.log('Start reconnecting', attemptNumber);
  });

  socket.on(SOCKET_EVENTS.CONNECT, () => {
    recentActiveSocketId = getSocketId();
    console.log('Connect success: ', recentActiveSocketId);

    // If not first connect attempt, reconnect will handle success handler
    if (!isSocketAttemptOnPageLoad) return;

    // After first connect, start requesting resync on every other reconnect
    controller.enableSocketResync(true);
    controller.updateSocketLastSyncTime();
  });

  socket.on(SOCKET_EVENTS.RECONNECT, (attemptNumber: number) => {
    // TODO: Do some clean up after reconnect
    recentActiveSocketId = getSocketId();
    console.log('Reconnect success: ', attemptNumber, recentActiveSocketId);

    // If first connect attempt, connect will handle success handler
    if (isSocketAttemptOnPageLoad) return;
  });

  socket.on(SOCKET_EVENTS.RECONNECT_ATTEMPT, (attemptNumber: number) => {
    // TODO: We may need to cap max reconnect attempts
    console.log('Reconnect attempt', attemptNumber);
  });

  socket.on(SOCKET_EVENTS.RECONNECT_ERROR, (err: object) => {
    controller.onError(SOCKET_ERRORS.RECONNECT_ERROR);

    console.error(
      `${SOCKET_EVENTS.RECONNECT_ERROR}: `,
      err,
      socketOptions.query,
      `recentSocket: ${recentActiveSocketId}`,
    );
  });

  socket.on(SOCKET_EVENTS.RECONNECT_FAILED, () => {
    // TODO: We may force user to refresh page
    // This handler only runs when number of reconnections has exceeded reconnectionAttempts.
    // If reconnectionAttempts is not supplied, it'll retry infinite times.
    console.error(`${SOCKET_EVENTS.RECONNECT_FAILED}`, socketOptions.query, `recentSocket: ${recentActiveSocketId}`);
  });

  /**
   * Handle general connection error
   */
  socket.on(SOCKET_EVENTS.CONNECT_ERROR, (err: object) => {
    // Only track initial connect error. Reconnect errors are handled in reconnect handler.
    if (!isSocketAttemptOnPageLoad) return;

    controller.onError(SOCKET_ERRORS.CONNECTION_ERROR);

    // Track initial connect error
    console.error(`${SOCKET_EVENTS.CONNECT_ERROR}: `, err, socketOptions);
  });

  socket.on(SOCKET_EVENTS.CONNECT_TIMEOUT, (err: object) => {
    console.log(`${SOCKET_EVENTS.CONNECT_TIMEOUT}: `, err, getSocketId());
  });

  socket.on(SOCKET_EVENTS.ERROR, (err: object) => {
    console.log(`${SOCKET_EVENTS.ERROR}: `, err);
  });

  // Track disconnect reason
  // Possible reasons (https://socket.io/docs/v2/client-api/#event-disconnect):
  // io server disconnect: The server has forcefully disconnected the socket with socket.disconnect()
  // io client disconnect: The socket was manually disconnected using socket.disconnect()
  // ping timeout: The server did not respond in the pingTimeout range
  // transport close: The connection was closed (example: the user has lost connection, or the network was changed from WiFi to 4G). e.x: collab server down.
  // transport error: The connection has encountered an error (example: the server was killed during a HTTP long-polling cycle)
  socket.on(SOCKET_EVENTS.DISCONNECT, (reason: string) => {
    console.log(`${SOCKET_EVENTS.DISCONNECT}: `, reason);
  });

  return socket;
};
